import { ActionIcon, Button, Group, Menu, useMantineTheme } from '@mantine/core'
import { IconArrowBack, IconCheck, IconChevronDown, IconPencil, IconSparkles, IconX } from '@tabler/icons-react'

import * as classes from '@/styles/AISplitButton.css'

type AIFieldState = 'idle' | 'loading' | 'pending'

type AISplitButtonProps = {
  fieldState: AIFieldState
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onCancel?: () => void
  /** When provided, idle dropdown shows "Generate" and "Guide…"; Guide opens the guide UI (e.g. popover). */
  onGuideClick?: () => void
  disabled?: boolean
}

function getButtonConfig(
  state: AIFieldState,
  handlers: { onAskSidekick: () => void; onAccept: () => void; onCancel?: () => void },
) {
  const configs = {
    loading: {
      text: 'Cancel',
      icon: IconX,
      onClick: handlers.onCancel ?? (() => {}),
      variant: 'default' as const,
    },
    pending: {
      text: 'Accept',
      icon: IconCheck,
      onClick: handlers.onAccept,
      variant: 'gradient' as const,
    },
    idle: {
      text: 'Ask Sidekick',
      icon: IconSparkles,
      onClick: handlers.onAskSidekick,
      variant: 'gradient' as const,
    },
  }

  return configs[state]
}

function IdleMenuItems({
  onAskSidekick,
  onGuideClick,
  disabled,
}: {
  onAskSidekick: () => void
  onGuideClick: () => void
  disabled?: boolean
}) {
  const mantineTheme = useMantineTheme()
  return (
    <>
      <Menu.Item
        leftSection={<IconSparkles size={16} stroke={1.5} color={mantineTheme.colors.blue[5]} />}
        onClick={onAskSidekick}
        disabled={disabled}>
        Generate
      </Menu.Item>
      <Menu.Item
        leftSection={<IconPencil size={16} stroke={1.5} color={mantineTheme.colors.gray[6]} />}
        onClick={onGuideClick}
        disabled={disabled}>
        Guide…
      </Menu.Item>
    </>
  )
}

function PendingMenuItems({
  onRegenerate,
  onReject,
  onGuideClick,
  disabled,
}: {
  onRegenerate?: () => void
  onReject: () => void
  onGuideClick?: () => void
  disabled?: boolean
}) {
  const mantineTheme = useMantineTheme()
  return (
    <>
      {onRegenerate && (
        <Menu.Item
          leftSection={<IconSparkles size={16} stroke={1.5} color={mantineTheme.colors.blue[5]} />}
          onClick={onRegenerate}
          disabled={disabled}>
          Regenerate
        </Menu.Item>
      )}

      {onGuideClick && (
        <Menu.Item
          leftSection={<IconPencil size={16} stroke={1.5} color={mantineTheme.colors.gray[6]} />}
          onClick={onGuideClick}
          disabled={disabled}>
          Guide…
        </Menu.Item>
      )}

      <Menu.Item
        leftSection={<IconArrowBack size={16} stroke={1.5} color={mantineTheme.colors.gray[5]} />}
        onClick={onReject}
        disabled={disabled}>
        Revert
      </Menu.Item>
    </>
  )
}

export function AISplitButton(props: AISplitButtonProps) {
  const { fieldState, onAskSidekick, onAccept, onReject, onRegenerate, onCancel, onGuideClick, disabled } = props

  const mainConfig = getButtonConfig(fieldState, { onAskSidekick, onAccept, onCancel })
  const isLoading = fieldState === 'loading'
  const isIdle = fieldState === 'idle'
  const isPending = fieldState === 'pending'
  const showIdleDropdown = isIdle && !!onGuideClick
  const canShowDropdown = !disabled && (isPending || showIdleDropdown) && !isLoading

  return (
    <Group wrap="nowrap" gap={0}>
      <Button
        size="sm"
        variant={mainConfig.variant}
        leftSection={mainConfig.icon ? <mainConfig.icon stroke={1.5} size={14} /> : undefined}
        onClick={mainConfig.onClick}
        disabled={isLoading ? false : disabled}
        className={classes.mainButton}>
        {mainConfig.text}
      </Button>

      <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon size="sm" disabled={!canShowDropdown} className={classes.dropdownButton}>
            <IconChevronDown size={14} stroke={1.5} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {isIdle && onGuideClick ? (
            <IdleMenuItems onAskSidekick={onAskSidekick} onGuideClick={onGuideClick} disabled={disabled} />
          ) : (
            <PendingMenuItems
              onRegenerate={onRegenerate}
              onReject={onReject}
              onGuideClick={onGuideClick}
              disabled={disabled}
            />
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}
